import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PortalAdminLayout from "./PortalAdminLayout";
import { useDemoMode } from "../../contexts/DemoModeContext";
import { getPortalAdminTools } from "./portalAdminTools";
import "./PortalDashboard.css";

const PortalDashboard = () => {
  const navigate = useNavigate();
  const { isDemoMode } = useDemoMode();
  const [stats, setStats] = useState({
    totalUsers: 0,
    subscribedShops: 0,
    subscribedFreelancers: 0,
    totalBookings: 0,
    ytdRevenue: 0,
  });
  const [loading, setLoading] = useState(true);
  const [decodedRoles, setDecodedRoles] = useState([]);
  const apiBaseUrl = (process.env.REACT_APP_API_BASE_URL || "").toLowerCase();
  const isDemoEnvironment =
    !apiBaseUrl ||
    apiBaseUrl.includes("api-demo") ||
    apiBaseUrl.includes("localhost") ||
    apiBaseUrl.includes("127.0.0.1");
  const showDemoTools = isDemoMode && isDemoEnvironment;

  useEffect(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join(""),
      );

      const decoded = JSON.parse(jsonPayload);
      const hasPortalAdminAccess =
        decoded.role === "portal_admin" ||
        decoded.role === "R6" ||
        decoded.role === "R8" ||
        (decoded.roles &&
          (decoded.roles.includes("portal_admin") ||
            decoded.roles.includes("R6") ||
            decoded.roles.includes("R8")));

      if (!hasPortalAdminAccess) {
        navigate("/dashboard");
        return;
      }

      setDecodedRoles(
        Array.isArray(decoded.roles) && decoded.roles.length
          ? decoded.roles
          : decoded.role
            ? [decoded.role]
            : [],
      );

      // Fetch dashboard stats
      fetchStats(token);
    } catch (error) {
      navigate("/login");
    }
  }, [navigate]);

  const fetchStats = async (token) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/dashboard/stats`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (response.ok) {
        const data = await response.json();
        setStats({
          totalUsers: data.users?.total || 0,
          subscribedShops: data.shops?.subscribed || 0,
          subscribedFreelancers: data.staff?.subscribedFreelancers || 0,
          totalBookings: data.bookings?.total || 0,
          ytdRevenue: data.subscriptions?.ytdRevenue || 0,
        });
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const portalAdminTools = getPortalAdminTools({ showDemoTools, userRoles: decodedRoles });
  const visibleAdminCards = portalAdminTools.filter(
    (card) => card.showInSidebar && card.available !== false,
  );

  const statCards = [
    {
      label: "Total Users",
      value: stats.totalUsers,
      icon: "👥",
      color: "#3b82f6",
      bgColor: "#eff6ff",
    },
    {
      label: "Active Subscriptions",
      value: stats.subscribedShops + stats.subscribedFreelancers,
      subtitle: `${stats.subscribedShops} shops, ${stats.subscribedFreelancers} freelancers`,
      icon: "✅",
      color: "#10b981",
      bgColor: "#ecfdf5",
    },
    {
      label: "Total Bookings",
      value: stats.totalBookings,
      icon: "📅",
      color: "#8b5cf6",
      bgColor: "#f5f3ff",
    },
    {
      label: "YTD Subscription Revenue",
      value: `$${stats.ytdRevenue.toFixed(2)}`,
      icon: "💰",
      color: "#8b5cf6",
      bgColor: "#f3e8ff",
    },
  ];

  const usersTool =
    portalAdminTools.find((tool) => tool.path === "/portal-admin/users") ||
    { icon: "👥", label: "User Management", path: "/portal-admin/users" };
  const businessesTool =
    portalAdminTools.find((tool) => tool.path === "/portal-admin/businesses") ||
    {
      icon: "🏬",
      label: "Business Management",
      path: "/portal-admin/businesses",
    };
  const revenueTool =
    portalAdminTools.find((tool) => tool.path === "/portal-admin/revenue") ||
    {
      icon: "💰",
      label: "Revenue Analytics",
      path: "/portal-admin/revenue",
    };

  return (
    <PortalAdminLayout>
      <div className="portal-dashboard-container">
        {/* Header Section */}
        <div className="portal-dashboard-header">
          <div className="portal-header-content">
            <div className="portal-header-icon">🎛️</div>
            <div>
              <h1>Portal Admin Dashboard</h1>
              <p>Manage and monitor your platform from one central location</p>
            </div>
          </div>
          <div className="portal-header-actions">
            <button
              className="portal-header-btn secondary"
              onClick={() => window.open("/", "_blank")}
            >
              🌐 View Site
            </button>
            <button
              className="portal-header-btn primary"
              onClick={() => navigate("/portal-admin/settings")}
            >
              ⚙️ Settings
            </button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="portal-stats-section">
          <h2 className="portal-section-title">📈 Platform Overview</h2>
          <div className="portal-stats-grid">
            {statCards.map((stat, index) => (
              <div
                key={index}
                className="portal-stat-card"
                style={{ "--stat-color": stat.color }}
              >
                <div
                  className="portal-stat-icon"
                  style={{ background: stat.bgColor }}
                >
                  <span>{stat.icon}</span>
                </div>
                <div className="portal-stat-content">
                  <div className="portal-stat-value">
                    {loading ? "..." : stat.value}
                  </div>
                  <div className="portal-stat-label">{stat.label}</div>
                  {stat.subtitle && (
                    <div
                      className="portal-stat-subtitle"
                      style={{
                        fontSize: "12px",
                        color: "#6b7280",
                        marginTop: "4px",
                      }}
                    >
                      {stat.subtitle}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Admin Cards Grid */}
        <div className="portal-admin-section">
          <h2 className="portal-section-title">🔧 Management Tools</h2>
          <div className="portal-admin-grid">
            {visibleAdminCards.map((card) => (
              <div
                key={card.path}
                className="portal-admin-card"
                onClick={() => navigate(card.path)}
                style={{
                  "--card-color": card.color,
                  "--card-bg": card.bgColor,
                }}
              >
                <div className="portal-card-accent"></div>
                <div
                  className="portal-card-icon"
                  style={{ background: card.bgColor }}
                >
                  <span>{card.icon}</span>
                </div>
                <div className="portal-card-content">
                  <h3>{card.title}</h3>
                  <p>{card.description}</p>
                </div>
                <div className="portal-card-arrow">→</div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions Footer */}
        <div className="portal-quick-actions">
          <div className="portal-quick-actions-content">
            <div className="portal-quick-icon">⚡</div>
            <div className="portal-quick-text">
              <h3>Quick Actions</h3>
              <p>Common administrative tasks at your fingertips</p>
            </div>
          </div>
          <div className="portal-quick-buttons">
            <button
              onClick={() => navigate(usersTool.path)}
              className="portal-quick-btn"
            >
              {usersTool.icon} {usersTool.label}
            </button>
            <button
              onClick={() => navigate(businessesTool.path)}
              className="portal-quick-btn"
            >
              {businessesTool.icon} {businessesTool.label}
            </button>
            <button
              onClick={() => navigate(revenueTool.path)}
              className="portal-quick-btn"
            >
              {revenueTool.icon} {revenueTool.label}
            </button>
          </div>
        </div>
      </div>
    </PortalAdminLayout>
  );
};

export default PortalDashboard;
